/* Theme custom file */
/**
 * Paginated list (alternate) — extends the native Horizon `PaginatedList` to add
 * a "pause every N pages" interruption to infinite scroll, surfaced with a manual
 * "Load more" button to resume, and to hold unavailable cards back on sorted sales.
 *
 * Why subclass instead of reimplement: the native fetch/observe/render machinery
 * lives in private methods on `PaginatedList`, so we let the base drive scrolling
 * and only gate it from the outside — disconnecting the scroll observer at each
 * N-page boundary and re-arming it on button click. See
 * docs/specs/plp.md and the BRD-381 infinite-scroll rework.
 *
 * Attributes:
 *   pages-before-pause  Integer N. Scroll auto-loads pages until the highest
 *                       loaded page number is a multiple of N, then pauses and
 *                       reveals the button. 0 (default) = uncapped infinite scroll.
 *                       Example N=3: pauses at pages 3, 6, 9 … each click resumes
 *                       auto-scroll for the next batch.
 *
 * Optional refs (in addition to the base): loadMoreButton.
 *
 * Note: the consuming section must include a `[ref="cardGallery"]` marker element
 * so the base's aspect-ratio helper constructs; without it the base throws on the
 * first appended page. The alternate product card has no card gallery of its own,
 * so a hidden marker stands in (the helper is design-mode only and inert for it).
 *
 * URL management (BRD-593, BRD-594): the base writes the loaded page into the URL
 * with `history.pushState('?page=N')` from private render methods we cannot override.
 * That produced two bugs — the back button walked page-by-page through the scroll
 * history instead of leaving the collection (593), and a refresh on a `?page=N` URL
 * re-rendered only from page N onward, dropping earlier products (594). We fix both
 * by intercepting `history.pushState` while connected and, for the base's page-only
 * URL updates, collapsing them to a `replaceState` with the `page` param stripped:
 * no history entries accumulate, and the visible URL never carries `?page`, so a
 * refresh always reloads page 1 and renders the full grid. Facet updates (which
 * change filter params, not just `page`) fall through to the native `pushState`, so
 * filter history is unaffected. The base still fetches pages correctly because it
 * derives page numbers from each card's `data-page`, not from the address bar.
 *
 * Deferred unavailable cards: on a sale sorted by the shopper the section flags the
 * grid `data-defer-unavailable`. CSS then hides every unavailable card until the grid
 * carries `data-all-pages-loaded`, and shows them after the available ones with
 * `order`. This class sets that flag once the last page is in the grid, flags the
 * grid `aria-busy` while the cards just loaded show nothing new, and re-arms the
 * scroll sentinel after each page: a page of hidden cards does not move it, so the
 * base's observer would otherwise never fire again.
 */
import PaginatedList from '@theme/paginated-list';
import { ThemeEvents } from '@theme/events';

export default class PaginatedListAlternate extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #gridObserver;

  /** @type {typeof history.pushState | null} */
  #nativePushState = null;

  /** @type {number} */
  #rearmFrame = 0;

  /** @type {number} Highest page loaded at the last re-arm, -1 after a re-render. */
  #rearmPage = -1;

  /** @type {number} */
  #rearmsWithoutProgress = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#patchHistory();

    this.refs.loadMoreButton?.addEventListener('click', this.#onLoadMore);

    // Always wired: a re-render can turn the pause or the deferred mode on later,
    // and both stay inert while their attribute is absent.
    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    this.#observeGrid();

    // Evaluate once on load in case the first page already sits on a boundary
    // (N = 1) or the page is short enough to satisfy the observer immediately.
    // The base observes the scroll sentinel on connect, so no re-arm is needed here.
    this.#evaluatePause();
    this.#evaluateDeferred();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#restoreHistory();
    this.#gridObserver?.disconnect();
    cancelAnimationFrame(this.#rearmFrame);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
    this.refs.loadMoreButton?.removeEventListener('click', this.#onLoadMore);
  }

  /**
   * Wrap `history.pushState` so the base's page-only URL updates become a
   * `replaceState` with `page` stripped. See the class doc comment for why.
   */
  #patchHistory() {
    // Guard against double-wrapping if another instance already patched.
    if (/** @type {any} */ (history.pushState).__paginationPatched) return;

    const nativePushState = history.pushState.bind(history);
    this.#nativePushState = nativePushState;

    /** @type {any} */
    const wrapper = (/** @type {any} */ state, /** @type {string} */ title, /** @type {string | URL} */ url) => {
      if (url != null && this.#isPageOnlyChange(url)) {
        const clean = new URL(url, window.location.href);
        clean.searchParams.delete('page');
        history.replaceState(state, title, clean.toString());
        return;
      }
      nativePushState(state, title, url);
    };
    wrapper.__paginationPatched = true;
    history.pushState = wrapper;
  }

  /** Restore the native `pushState` if this instance installed the wrapper. */
  #restoreHistory() {
    if (this.#nativePushState && /** @type {any} */ (history.pushState).__paginationPatched) {
      history.pushState = this.#nativePushState;
    }
    this.#nativePushState = null;
  }

  /**
   * Whether a proposed URL differs from the current one only by the `page` param —
   * the signature of the base's infinite-scroll updates. Facet changes touch filter
   * params and so return false, passing through to the native `pushState`.
   * @param {string | URL} url
   * @returns {boolean}
   */
  #isPageOnlyChange(url) {
    const next = new URL(url, window.location.href);
    const current = new URL(window.location.href);

    if (next.origin !== current.origin || next.pathname !== current.pathname) return false;

    next.searchParams.delete('page');
    current.searchParams.delete('page');
    next.searchParams.sort();
    current.searchParams.sort();

    return next.searchParams.toString() === current.searchParams.toString();
  }

  /** @returns {number} The configured N, or 0 when uncapped. */
  get #pagesBeforePause() {
    return Number(this.getAttribute('pages-before-pause')) || 0;
  }

  /**
   * Observe the list rather than the grid: a re-render morphs cards in place, can swap
   * the grid for the empty state and back, and the morph never calls `updatedCallback`
   * on a live node (it hands the parsed copy to its hook). Records outside the grid
   * are ignored in the callback. The attributes we set ourselves (`aria-busy`,
   * `data-all-pages-loaded`) stay out of the filter so the observer never re-enters.
   */
  #observeGrid() {
    this.#gridObserver?.disconnect();

    this.#gridObserver = new MutationObserver(this.#onGridChange);
    this.#gridObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-unavailable', 'data-defer-unavailable', 'data-last-page'],
    });
  }

  /** @returns {number} Highest page number currently rendered in the grid. */
  #maxLoadedPage() {
    const { grid } = this.refs;
    if (!grid) return 0;

    let max = 0;
    for (const card of grid.querySelectorAll('[ref="cards[]"]')) {
      const page = Number(card.getAttribute('data-page'));
      if (page > max) max = page;
    }
    return max;
  }

  /** @returns {number} Total page count, from the grid's data-last-page. */
  #lastPage() {
    return Number(this.refs.grid?.dataset.lastPage) || 0;
  }

  /** @param {MutationRecord[]} records */
  #onGridChange = (records) => {
    const { grid } = this.refs;
    if (!grid) return;

    const gridRecords = records.filter(
      (record) => grid.contains(record.target) || [...record.addedNodes].includes(grid)
    );
    if (!gridRecords.length) return;

    // The base appends a page as a childList record on the grid. A grid inserted,
    // cards removed or a watched attribute changed is a re-render, evaluated on the
    // whole grid. Anything else happens inside a card and is none of our business:
    // reacting to it would re-arm, and clear the cache, without a page to show for it.
    const rerender = gridRecords.some(
      (record) =>
        record.type === 'attributes' ||
        [...record.addedNodes].includes(grid) ||
        (record.target === grid && record.removedNodes.length > 0)
    );
    const added = rerender
      ? undefined
      : gridRecords.filter((record) => record.target === grid).flatMap((record) => [...record.addedNodes]);
    if (added && !added.length) return;

    this.#evaluatePause();

    // The base caches fetched pages by number only, and a page of the previous URL
    // still in flight when the filter changed lands in that cache after the base
    // cleared it. Clear again once the re-render is in, before the chain reads it.
    if (rerender) {
      this.pages.clear();
      this.#rearmPage = -1;
    }

    if (this.#evaluateDeferred(added)) this.#scheduleRearm();
  };

  /**
   * Sorted sale grids (`data-defer-unavailable`) hide unavailable cards until the
   * last page is loaded, then CSS `order` shows them after every available card.
   * Flags the grid busy while the cards just loaded show nothing new.
   * @param {Node[]} [added] Cards of the page just appended, or the whole grid when omitted.
   * @returns {boolean} Whether more pages are still needed.
   */
  #evaluateDeferred(added) {
    const { grid } = this.refs;
    if (!grid?.hasAttribute('data-defer-unavailable') || grid.hasAttribute('data-all-pages-loaded')) return false;

    if (this.#maxLoadedPage() >= this.#lastPage()) {
      grid.setAttribute('data-all-pages-loaded', '');
      grid.removeAttribute('aria-busy');
      return false;
    }

    const items = added ?? [...grid.children];
    const showsCard = items.some((item) => item instanceof Element && !item.querySelector('[data-unavailable]'));
    if (showsCard) {
      grid.removeAttribute('aria-busy');
    } else {
      grid.setAttribute('aria-busy', 'true');
    }
    return true;
  }

  /**
   * A page of hidden cards does not move the scroll sentinel, so the base's observer
   * gets no new intersection and scrolling stalls. Re-observing the sentinel makes it
   * report its current state again; the base only renders when it intersects, so a
   * sentinel scrolled out of view is a no-op. One frame coalesces the burst of
   * records a re-render produces, so at most one page render is started per change.
   *
   * The base reads the next page number off `refs.cards`, which the component only
   * refreshes from an observer it attaches once the tab is visible and idle. Re-arming
   * a frame after an append gets ahead of that: with stale refs the base would append
   * a page it already holds, from cache, and loop on it. So the refs are refreshed
   * here, and a re-arm that brings no new page is retried at most twice; a scroll
   * still reaches the base's own observer either way.
   */
  #scheduleRearm() {
    cancelAnimationFrame(this.#rearmFrame);
    this.#rearmFrame = requestAnimationFrame(() => {
      this.updatedCallback();

      const { viewMoreNext, loadMoreButton } = this.refs;
      if (!viewMoreNext || !this.infinityScrollObserver || loadMoreButton?.hidden === false) return;

      const maxPage = this.#maxLoadedPage();
      this.#rearmsWithoutProgress = maxPage === this.#rearmPage ? this.#rearmsWithoutProgress + 1 : 0;
      this.#rearmPage = maxPage;
      if (this.#rearmsWithoutProgress > 2) return;

      this.infinityScrollObserver.unobserve(viewMoreNext);
      // An entry still queued from the previous observation would be delivered with
      // the fresh one and start the same page twice.
      this.infinityScrollObserver.takeRecords();
      this.infinityScrollObserver.observe(viewMoreNext);
    });
  }

  /** Pause on an N-page boundary (more pages remaining), otherwise stay/resume active. */
  #evaluatePause = () => {
    const n = this.#pagesBeforePause;
    if (n <= 0) return;

    const maxPage = this.#maxLoadedPage();
    const lastPage = this.#lastPage();
    const atBoundary = maxPage > 0 && maxPage % n === 0;
    const hasMore = lastPage === 0 || maxPage < lastPage;

    if (atBoundary && hasMore) {
      this.#pause();
    } else {
      this.#setButtonVisible(false);
    }
  };

  /** Stop auto-loading and reveal the button. */
  #pause() {
    this.infinityScrollObserver?.disconnect();
    this.#setButtonVisible(true);
  }

  /** Hide the button and re-arm the scroll observer for the next batch. */
  #onLoadMore = () => {
    this.#setButtonVisible(false);

    const { viewMoreNext, viewMorePrevious } = this.refs;
    if (viewMoreNext) this.infinityScrollObserver?.observe(viewMoreNext);
    if (viewMorePrevious) this.infinityScrollObserver?.observe(viewMorePrevious);
  };

  /**
   * The re-render that follows lands later, as a morph. With pages 2+ loaded it
   * removes them, and those grid records drive the re-evaluation (#onGridChange);
   * re-arming now would fetch page N+1 of the new URL under the old page 1. With only
   * page 1 loaded the morph may change nothing the observer sees (same cards under
   * another sort), so re-arm now: page 2 of the new URL is the right next page either
   * way, and the base has already dropped its cached pages.
   */
  #onFilterUpdate = () => {
    const singlePage = this.#maxLoadedPage() <= 1;

    requestAnimationFrame(() => {
      this.#evaluatePause();
      if (singlePage && this.#evaluateDeferred()) this.#scheduleRearm();
    });
  };

  /** @param {boolean} visible */
  #setButtonVisible(visible) {
    const { loadMoreButton } = this.refs;
    if (loadMoreButton) loadMoreButton.hidden = !visible;
  }
}

if (!customElements.get('results-list-alternate')) {
  customElements.define('results-list-alternate', PaginatedListAlternate);
}
